import { BannerState } from '../types/banner-state.enum';
import { DisplayState } from '../types/display-state.enum';

/**
 * Every user-facing string in one place. The page ships English-only in v1
 * (internal ops page); swapping this object for translation keys is the whole
 * localisation job, which is why nothing is inlined in templates and why every
 * entry is a plain string with `{placeholders}` rather than a function — plural
 * selection is a property of the target language, not of this catalogue.
 */
export const SYSTEM_HEALTH_COPY = {
  states: {
    [DisplayState.Available]: 'Available',
    [DisplayState.Unavailable]: 'Unavailable',
    [DisplayState.Unknown]: 'Unknown',
  } satisfies Record<DisplayState, string>,
  banner: {
    [BannerState.Loading]: { title: 'Checking system status…', subtitle: 'Fetching the latest health data.' },
    [BannerState.Empty]: {
      title: 'No dependencies are being monitored',
      subtitle: 'The status service answered, but reported nothing to monitor.',
    },
    [BannerState.Available]: { title: 'All systems available', subtitle: 'Every monitored dependency is responding.' },
    [BannerState.Delayed]: {
      title: 'All systems available (some checks delayed)',
      subtitle: 'No outage detected, but some dependencies have not reported recently.',
    },
    [BannerState.Partial]: { title: 'Partial service disruption', subtitle: 'Some dependencies are unavailable.' },
    [BannerState.Major]: { title: 'Major service disruption', subtitle: 'Several dependencies are unavailable.' },
    [BannerState.Offline]: {
      title: 'Cannot reach the status service',
      subtitle: 'Showing the last data received.',
    },
    [BannerState.AuthError]: {
      title: 'Internal view not available',
      subtitle:
        'The status service is not exposing the internal view for this deployment. Showing the last data received.',
    },
  } satisfies Record<BannerState, { title: string; subtitle: string }>,
  summary: {
    issuesTitle: 'Current issues',
    allAvailableOne: 'The monitored service is available.',
    allAvailableMany: 'All {count} monitored services are available.',
    othersAvailableOne: 'The other monitored service is available.',
    othersAvailableMany: 'All other {count} monitored services are available.',
  },
  report: {
    title: 'Last 24 hours',
    allAvailable: 'All monitored services were available during the last 24 hours.',
    othersAvailable: 'All other monitored services were available.',
    unavailable: 'The last-24-hours report is temporarily unavailable.',
    unavailableWord: 'unavailable',
    monitoringGapLabel: 'Status monitoring',
    noDataWord: 'no data',
    ongoingSuffix: '– ongoing',
    servicesSeparator: ', ',
    windowSeparator: '; ',
  },
  labels: {
    lastChecked: 'Last checked',
    unavailableSince: 'unavailable since {time}',
    updated: 'Updated',
    refreshIn: 'refresh in',
    dataAge: 'Data from',
    neverChecked: 'not yet checked',
    serviceState: '{service}: {state}',
  },
  footer: '© {year} {company}. Internal status page — not for distribution.',
} as const;
